import React, { useEffect, useState } from "react";
import BranchListing from "./BranchListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/BranchSlice";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import AddBranchFormWrapper from "../Add/AddBranchFormWrapper";
import { useGetBranchesQuery } from "src/services/BranchServices";
import { useDeleteBranchMutation } from "src/services/BranchServices";
import { showToast } from "src/utils/showToaster";
import EditBranchFormWrapper from "../Edit/EditBranchFormWrapper";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const BranchListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { totalItems, page, rowsPerPage, searchValue, isTableLoading, items } =
    useSelector((state: RootState) => state.branch);

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditFrom, setIsOpenEditForm] = useState(false);
  const [branchId, setBranchId] = useState('')


  const [deleteBranch] = useDeleteBranchMutation()

  const { data, isLoading, isFetching } = useGetBranchesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "branchAddress",
      "city",
      "state",
      "country",
      "lat",
      "long",
      "isActive",
      "isDeleted",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setBranchId(row?._id)
          setIsOpenEditForm(true)
        },
        moduleName: UserModuleNameTypes.BRANCHES,
        actionName: UserModuleActionTypes.EDIT,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Branch ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteBranch(row?._id).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        moduleName: UserModuleNameTypes.BRANCHES,
        actionName: UserModuleActionTypes.DELETE,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "branchName",
      headerName: "Branch Name",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "capitalize",
      renderCell: (row: any) => <span> {row.branchName} </span>,
    },
    {
      field: "branchCode",
      headerName: "Branch Code",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "capitalize",
      renderCell: (row: any) => <span> {row.branchCode} </span>,
    },
    {
      field: "countryLabel",
      headerName: "Country",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "capitalize",
      renderCell: (row: any) => <span> {row.countryLabel} </span>,
    },
    {
      field: "stateLabel",
      headerName: "State",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "capitalize",
      renderCell: (row: any) => <span> {row.stateLabel} </span>,
    },
    {
      field: "cityLabel",
      headerName: "City",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "capitalize",
      renderCell: (row: any) => <span> {row.cityLabel} </span>,
    },
    {
      field: "lat-long",
      headerName: "Lat-Long",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row: any) => (
        <div>
          {" "}
          {row?.lat} - {row?.long}{" "}
        </div>
      ),
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      <BranchListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.BRANCHES,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddBranchFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditFrom ? (
        <EditBranchFormWrapper branchId={branchId} onClose={() => setIsOpenEditForm(false)} />
      ) : null}
    </div>
  );
};

export default BranchListingWrapper;
